/**
 * Utility functions and constants for issue-related operations
 */

import { issueDestinationAPI } from '../api/issueDestinationAPI';

// Cache for destination options to avoid repeated API calls
let parentDestinationCache = null;
let parentCacheTimestamp = null;
let subcategoryCache = {}; // Cache by parent ID
let subcategoryCacheTimestamp = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Gets parent destination options from the API with caching
 * @param {boolean} useCache Whether to use cached data if available
 * @returns {Promise<Array>} Array of parent destination options
 */
export const getParentDestinationOptions = async (useCache = true) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (useCache && parentDestinationCache && parentCacheTimestamp && (now - parentCacheTimestamp < CACHE_DURATION)) {
        return parentDestinationCache;
    }
    
    try {
        const destinations = await issueDestinationAPI.getOptions();
        
        const options = [
            { value: '', label: 'Select destination...', type: 'placeholder' }
        ];
        
        destinations.forEach(dest => {
            options.push({
                value: dest._id,
                label: dest.name,
                type: 'destination',
                full_name: dest.full_name || dest.name
            });
        });
        
        // Cache the results
        parentDestinationCache = options;
        parentCacheTimestamp = now;
        
        return options;
    } catch (error) {
        console.error('Error fetching parent destination options:', error);
        return [
            { value: '', label: 'Select destination...', type: 'placeholder' },
            { value: 'fallback', label: 'Error loading destinations', type: 'error' }
        ];
    }
};


/**
 * Find a parent destination option by its ID from API
 * @param {string} id The destination ID to search for
 * @param {Array} options Optional pre-loaded options to search in
 * @returns {Promise<Object|undefined>} The matching option or undefined if not found
 */
export const findParentDestinationOption = async (id, options = null) => {
    const searchOptions = options || await getParentDestinationOptions();
    return searchOptions.find(option => option.value === id);
};

/**
 * Get subcategory options for a specific parent destination
 * @param {string} parentDestinationId The parent destination ID
 * @param {boolean} useCache Whether to use cached data if available
 * @returns {Promise<Array>} Array of subcategory options for the parent
 */
export const getSubcategoryOptions = async (parentDestinationId, useCache = true) => {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (useCache && subcategoryCache[parentDestinationId] && subcategoryCacheTimestamp[parentDestinationId] && 
        (now - subcategoryCacheTimestamp[parentDestinationId] < CACHE_DURATION)) {
        return subcategoryCache[parentDestinationId];
    }
    
    try {
        const subcategories = await issueDestinationAPI.subcategories.getOptions(parentDestinationId);
        
        const options = [
            { value: '', label: 'Select subcategory...', type: 'placeholder' }
        ];
        
        subcategories.forEach(sub => {
            options.push({
                value: sub._id,
                label: sub.name,
                type: 'subcategory',
                parent_id: sub.parent_destination,
                full_name: sub.full_name || sub.name,
                parent_destination_name: sub.parent_destination_name
            });
        });
        
        // Cache the results by parent ID
        subcategoryCache[parentDestinationId] = options;
        subcategoryCacheTimestamp[parentDestinationId] = now;
        
        return options;
    } catch (error) {
        console.error('Error fetching subcategory options:', error);
        return [
            { value: '', label: 'Select subcategory...', type: 'placeholder' },
            { value: 'fallback', label: 'Error loading subcategories', type: 'error' }
        ];
    }
};

/**
 * Find a subcategory option by its ID
 * @param {string} id The subcategory ID to search for
 * @param {string} parentId The parent destination ID to limit search
 * @returns {Promise<Object|undefined>} The matching subcategory option
 */
export const findSubcategoryOption = async (id, parentId = null) => {
    try {
        if (parentId) {
            const options = await getSubcategoryOptions(parentId);
            return options.find(option => option.value === id);
        } else {
            // Search in all subcategories if no parent specified
            const allSubcategories = await issueDestinationAPI.subcategories.getOptions();
            const subcategory = allSubcategories.find(sub => sub._id === id);
            if (subcategory) {
                return {
                    value: subcategory._id,
                    label: subcategory.name,
                    type: 'subcategory',
                    parent_id: subcategory.parent_destination,
                    full_name: subcategory.full_name || subcategory.name,
                    parent_destination_name: subcategory.parent_destination_name
                };
            }
        }
        return undefined;
    } catch (error) {
        console.error('Error finding subcategory option:', error);
        return undefined;
    }
};

/**
 * Clear the destination options cache (useful for refreshing data)
 */
export const clearDestinationCache = () => {
    parentDestinationCache = null;
    parentCacheTimestamp = null;
    subcategoryCache = {};
    subcategoryCacheTimestamp = {};
};

/**
 * Format destination display name for UI
 * @param {Object} destination The destination option object
 * @returns {string} Formatted display name
 */
export const formatDestinationDisplayName = (destination) => {
    if (!destination) return '';
    
    if (destination.type === 'subcategory') {
        return destination.label; // Already formatted as "Parent > Subcategory"
    }
    
    return destination.full_name || destination.label;
};

/**
 * Check if the parent destination cache is available
 * @returns {boolean} True if cache is available and not expired
 */
export const isParentDestinationCacheAvailable = () => {
    const now = Date.now();
    return parentDestinationCache && parentCacheTimestamp && (now - parentCacheTimestamp < CACHE_DURATION);
};

/**
 * Check if the subcategory cache is available for a specific parent
 * @param {string} parentId The parent destination ID
 * @returns {boolean} True if cache is available and not expired
 */
export const isSubcategoryCacheAvailable = (parentId) => {
    const now = Date.now();
    return subcategoryCache[parentId] && subcategoryCacheTimestamp[parentId] && 
           (now - subcategoryCacheTimestamp[parentId] < CACHE_DURATION);
};